import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress,
  ToggleButton, ToggleButtonGroup, Stepper, Step, StepLabel, IconButton, InputAdornment
} from '@mui/material';
import { Email as EmailIcon, Sms as SmsIcon, Lock as LockIcon, RemoveRedEye, VisibilityOff } from '@mui/icons-material';
import { authAPI } from '../services/api';

const ForgotPassword = ({ onBackToLogin }) => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    identifier: '',
    code: '',
    password: '',
    password_confirmation: '',
    reset_token: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEmail, setIsEmail] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const emailCheck = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.identifier);
    setIsEmail(emailCheck);

    try {
      let response;
      if (emailCheck) {
        response = await authAPI.sendResetCode({ method: 'email', email: formData.identifier });
      } else {
        response = await authAPI.sendPhoneResetCode({ phone_number: formData.identifier });
      }
      
      if (response.data.debug_code) {
        setMessage(`${response.data.message} ${response.data.debug_code}`);
      } else {
        setMessage(`Verification code sent to your ${emailCheck ? 'email' : 'phone'}`);
      }
      setStep(1);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;
      if (isEmail) {
        response = await authAPI.verifyResetCode({ method: 'email', email: formData.identifier, code: formData.code });
      } else {
        response = await authAPI.verifyPhoneResetCode({ phone_number: formData.identifier, code: formData.code });
      }
      setFormData({...formData, reset_token: response.data.reset_token});
      setMessage('Code verified successfully');
      setStep(2);
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isEmail) {
        await authAPI.resetPasswordWithCode({
          reset_token: formData.reset_token,
          password: formData.password,
          password_confirmation: formData.password_confirmation
        });
      } else {
        await authAPI.resetPasswordWithPhoneCode({
          reset_token: formData.reset_token,
          password: formData.password,
          password_confirmation: formData.password_confirmation
        });
      }
      
      setMessage('Password reset successfully! You can now login.');
      setTimeout(() => onBackToLogin(), 2000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Send Code', 'Verify Code', 'Reset Password'];

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            {step === 0 && <EmailIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />}
            {step === 1 && <SmsIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />}
            {step === 2 && <LockIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />}
            <Typography variant="h5" gutterBottom>
              Reset Password
            </Typography>
          </Box>

          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {step === 0 && (
            <Box component="form" onSubmit={handleSendCode}>
              <TextField
                fullWidth
                type="text"
                label="Email or Phone Number"
                placeholder="admin@example.com or 0772123456"
                value={formData.identifier}
                onChange={(e) => setFormData({...formData, identifier: e.target.value})}
                required
                disabled={loading}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Code'}
              </Button>
            </Box>
          )}

          {step === 1 && (
            <Box component="form" onSubmit={handleVerifyCode}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the verification code sent to your {isEmail ? 'email' : 'phone'}
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                required
                disabled={loading}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify Code'}
              </Button>
            </Box>
          )}

          {step === 2 && (
            <Box component="form" onSubmit={handleResetPassword}>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="New Password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                disabled={loading}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <RemoveRedEye />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm New Password"
                value={formData.password_confirmation}
                onChange={(e) => setFormData({...formData, password_confirmation: e.target.value})}
                required
                disabled={loading}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <RemoveRedEye />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
            </Box>
          )}

          <Button
            fullWidth
            variant="text"
            onClick={onBackToLogin}
            disabled={loading}
          >
            Back to Login
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForgotPassword;